import { ArticleManager, Author } from '@benzinga/article-manager';
import { BasicNewsManager, StoryObject } from '@benzinga/basic-news-manager';
import { ContentManager, LayoutResponse, NodeLayout } from '@benzinga/content-manager';
import { safeTimeout } from '@benzinga/safe-await';
import { loadServerSideBlockData } from '@benzinga/blocks-utils';
import { DateTime } from 'luxon';
import { MetaProps } from '@benzinga/seo';
import { NavigationHeaderProps } from '@benzinga/navigation-ui';

import { Session } from '@benzinga/session';

interface AuthorServerSideProps {
  author?: Author | null;
  headerProps?: NavigationHeaderProps;
  excludeContentTypes: string[];
  layout?: NodeLayout;
  metaProps?: MetaProps | null;
  news: StoryObject[];
  type: string | null;
}

export const getAuthorServerSideProps = async (
  session: Session,
  authorSlug: string,
  baseUrl = 'https://www.benzinga.com',
  excludeContentTypes: string[] = [],
  type: string | null = null,
): Promise<{ props: AuthorServerSideProps }> => {
  // ToDo: Update the content data source.  The current API has limited properties and is also limited to BZ (not PRO) content and will throw a 404 otherwise
  const headerPropsDefault = {
    hideBanner: true,
    hideMenuBar: true,
    hideQuoteBar: true,
    hideSearchBar: true,
    isMainDrawerVisible: true,
  };

  const articleManager = session.getManager(ArticleManager);
  const basicNewsManager = session.getManager(BasicNewsManager);
  const contentManager = session.getManager(ContentManager);
  const nodeId = '204614';
  const getLayoutReq = contentManager.getNodeLayout(nodeId);
  const layoutRes = await safeTimeout(getLayoutReq, 3000);
  const layout: NodeLayout = (layoutRes.ok as unknown as NodeLayout) ?? null;

  let headerProps;

  if (type === 'benzinga_wire_india') {
    const menuResponse = await session.getManager(ContentManager).getNavigation('money_india');
    const menuData = menuResponse?.ok ?? null;
    headerProps = {
      ...headerPropsDefault,
      hideMenuBar: false,
      hideTopBar: true,
      menuData: menuData,
    };
  } else {
    headerProps = headerPropsDefault;
  }
  if (layout && layout?.sidebar && Array.isArray(layout?.sidebar?.blocks)) {
    if (type === 'benzinga_wire_india') {
      const sidebarReq = await session.getManager(ContentManager).getWordpressPost(235418);
      const sidebar = sidebarReq.ok ?? null;
      if (Array.isArray(sidebar?.blocks)) {
        layout.sidebar.blocks = await loadServerSideBlockData(session, sidebar.blocks);
      }
    } else {
      layout.sidebar.blocks = await loadServerSideBlockData(session, layout.sidebar.blocks);
    }
  }

  const author = await articleManager.getAuthorByPath(authorSlug);

  let authorArticlesRes: StoryObject[] = [];
  let robots: string | null = null;

  if (author.ok) {
    author.ok.picture = author.ok?.picture
      ? `https://cdn.benzinga.com/${author.ok.picture}`
      : 'https://cdn.benzinga.com/sites/all/modules/custom/bz_user/images/no_picture.png';
    author.ok.twitter = author.ok?.twitter ? `https://twitter.com/${author.ok.twitter}` : null;
    author.ok.facebook = author.ok?.facebook ? `https://facebook.com/${author.ok.facebook}` : null;

    const authorArticlesReq = await basicNewsManager.simplyQueryNews(
      {
        userId: author.ok.uid,
      },
      {
        allOriginalArticles: true,
        excludeContentTypes,
        limit: 20,
        type: type ?? '',
      },
    );

    if (Array.isArray(authorArticlesReq.ok) && authorArticlesReq.ok.length) {
      authorArticlesRes = authorArticlesReq.ok;
      let diff = 0;

      if (authorArticlesRes[0]) {
        const latestArticleDate: Date = authorArticlesRes[0].createdAt;
        diff = DateTime.now().diff(DateTime.fromISO(latestArticleDate.toString()), ['days']).days;
      }

      if (authorArticlesRes.length < 15 && diff > 365) {
        robots = 'noindex';
      }
    }
  }

  const authorName =
    author.ok?.firstname && author.ok?.lastname
      ? `${author.ok?.firstname} ${author.ok?.lastname}`
      : author.ok?.name
        ? author.ok.name
        : authorSlug;

  return {
    props: {
      author: author.ok ?? null,
      excludeContentTypes,
      headerProps,
      layout: layout,
      metaProps: {
        canonical: author.ok?.profileUrl
          ? `${author.ok.profileUrl.replace('https://www.benzinga.com', baseUrl)}`
          : null,
        description: `${author.ok?.about || `${authorName} is a ${author.ok?.byLine || 'Benzinga Contributor'}`}`,
        robots,
        title: `Articles written by ${authorName}`,
      },
      news: authorArticlesRes,
      type,
    },
  };
};
